import { useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useBookCoachingCredit, useCoachingLesson } from "@bw/shared/hooks";
import type {
  CoachingLesson,
  CoachingCreditBookingInput,
  CoachingSession,
} from "@bw/shared/api";
import { coachingTime } from "./CoachingProposalPanel";
import { CoachingCancellationPanel } from "./CoachingCancellationPanel";

export function CoachingLessonPanel({
  acceptanceId,
}: {
  acceptanceId: string;
}) {
  const query = useCoachingLesson(acceptanceId);
  const lesson = query.retainedData;
  const matches =
    lesson?.fulfillment.acceptance_id?.toLowerCase() ===
    acceptanceId.toLowerCase();
  const current = query.isCurrent && matches;
  return (
    <>
      {query.fetchStatus === "paused" ? (
        <Alert severity="info" role="status">
          Waiting for a connection to refresh your lesson.
        </Alert>
      ) : query.isError || (query.isCurrent && !matches) ? (
        <Alert severity="error">
          This lesson is unavailable. Refresh to check your access.
        </Alert>
      ) : !current ? (
        <Typography role="status">Loading lesson…</Typography>
      ) : null}
      {lesson && matches ? (
        <Box hidden={!current} inert={!current}>
          <LessonContents
            acceptanceId={acceptanceId}
            lesson={lesson}
            canAct={query.canAct}
          />
        </Box>
      ) : null}
    </>
  );
}
function LessonContents({
  acceptanceId,
  lesson,
  canAct,
}: {
  acceptanceId: string;
  lesson: CoachingLesson;
  canAct: () => boolean;
}) {
  return (
    <Stack component="section" aria-label="Lesson" spacing={1.5} sx={{ mt: 2 }}>
      <Typography sx={{ fontWeight: 700 }}>
        {lesson.fulfillment.state === "held"
          ? "Held — awaiting funding"
          : lesson.fulfillment.state === "released"
            ? "Released"
            : lesson.fulfillment.state === "superseded"
              ? "Rescheduled — history"
            : "Funded"}
      </Typography>
      <Typography variant="body2">
        Times in {lesson.schedule.timezone}
      </Typography>
      {lesson.coach_assignment_state === "unassigned" ? (
        <Alert severity="info">
          The business is assigning a coach. Your booking, payment, and accepted
          terms remain unchanged.
        </Alert>
      ) : null}
      <Stack spacing={1}>{lesson.sessions.map((session) => (
        <Paper key={session.session_key} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}>
            <Stack spacing={0.25}><Typography sx={{ fontWeight: 650 }}>
              {coachingTime(session.starts_at, lesson.schedule.timezone)} – {coachingTime(session.ends_at, lesson.schedule.timezone)}
            </Typography><Typography variant="body2" color="text.secondary">
              {session.court_ids.length ? session.court_ids.map((id) => lesson.courts.find((court) => court.court_id === id)?.name ?? "Court unavailable").join(", ") : "No court required"}
            </Typography></Stack>
            <Chip size="small" label={sessionOutcomeLabel(session.outcome)} color={session.outcome === "delivered" ? "success" : session.outcome === "learner_no_show" ? "warning" : "default"} />
          </Stack>
        </Paper>
      ))}</Stack>
      {lesson.schedule.kind === "flexible_package" ? (
        <>
          <Typography variant="body2">
            {lesson.schedule.session_duration_minutes} minutes per session ·{" "}
            {lesson.schedule.required_court_count} courts required
          </Typography>
          <Typography variant="body2">
            Book from{" "}
            {lesson.schedule.booking_opens_at
              ? coachingTime(
                  lesson.schedule.booking_opens_at,
                  lesson.schedule.timezone,
                )
              : "—"}{" "}
            until{" "}
            {lesson.schedule.booking_closes_at
              ? coachingTime(
                  lesson.schedule.booking_closes_at,
                  lesson.schedule.timezone,
                )
              : "—"}
            .
          </Typography>
          <Typography variant="body2">
            Complete sessions by{" "}
            {lesson.schedule.sessions_complete_by
              ? coachingTime(
                  lesson.schedule.sessions_complete_by,
                  lesson.schedule.timezone,
                )
              : "—"}
            .
          </Typography>
          {lesson.credits.map((credit, index) => (
            <Typography key={credit.credit_key} variant="body2">
              Session credit {index + 1}: {credit.state}
            </Typography>
          ))}
        </>
      ) : null}
      {lesson.can_book_sessions ? (
        <CoachingBookingForm
          key={`${acceptanceId}:${lesson.accepted_revision}`}
          acceptanceId={acceptanceId}
          lesson={lesson}
          canAct={canAct}
        />
      ) : null}
      <CoachingCancellationPanel acceptanceId={acceptanceId} lesson={lesson} canAct={canAct} />
    </Stack>
  );
}
const sessionOutcomeLabel = (outcome: CoachingSession["outcome"]) => ({ scheduled: "Scheduled", cancelled: "Cancelled",
  delivered: "Delivered", learner_no_show: "Learner no-show", superseded: "Rescheduled" })[outcome];

const bookingSchema = z.object({
  credit: z.string().min(1),
  start: z.string().min(1),
  end: z.string().min(1),
  courts: z.array(z.string()),
});
type BookingForm = z.infer<typeof bookingSchema>;
function CoachingBookingForm({
  acceptanceId,
  lesson,
  canAct,
}: {
  acceptanceId: string;
  lesson: CoachingLesson;
  canAct: () => boolean;
}) {
  const book = useBookCoachingCredit();
  const retry = useRef<{
    fingerprint: string;
    body: CoachingCreditBookingInput;
  } | null>(null);
  const form = useForm<BookingForm>({
    resolver: zodResolver(bookingSchema),
    defaultValues: { credit: "", start: "", end: "", courts: [] },
  });
  const selected = form.watch("courts");
  const busy = book.isPending || form.formState.isSubmitting;
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  return (
    <Stack
      component="form"
      aria-label="Book session"
      spacing={1.5}
      onSubmit={form.handleSubmit(async (values) => {
        if (!canAct()) return;
        const start = new Date(`${values.start}Z`),
          end = new Date(`${values.end}Z`);
        if (
          !Number.isFinite(start.getTime()) ||
          !Number.isFinite(end.getTime())
        ) {
          form.setError("start", {
            message: "Enter valid UTC dates and times.",
          });
          return;
        }
        const fingerprint = JSON.stringify(values);
        const body: CoachingCreditBookingInput =
          retry.current?.fingerprint === fingerprint
            ? retry.current.body
            : {
                credit_key: values.credit,
                expected_revision: lesson.accepted_revision,
                starts_at: start.toISOString(),
                ends_at: end.toISOString(),
                court_ids: values.courts,
                idempotency_key: crypto.randomUUID(),
              };
        retry.current = { fingerprint, body };
        try {
          await book.mutateAsync([acceptanceId, body]);
          form.reset();
          retry.current = null;
        } catch {
          /* Shown below. */
        }
      })}
    >
      <Typography variant="subtitle2">
        Published availability ({lesson.published_availability.timezone})
      </Typography>
      {lesson.published_availability.windows.length ? (
        lesson.published_availability.windows.map((window, index) => (
          <Typography key={index} variant="body2">
            {days[window.day_of_week]} {window.start_time}–{window.end_time}
          </Typography>
        ))
      ) : (
        <Typography variant="body2">
          No availability is published. Contact the coach.
        </Typography>
      )}
      <TextField
        select
        label="Session credit"
        value={form.watch("credit")}
        onChange={(event) => form.setValue("credit", event.target.value)}
        size="small"
        disabled={busy}
        required
      >
        {lesson.credits.map((credit, index) =>
          credit.state === "available" ? (
            <MenuItem key={credit.credit_key} value={credit.credit_key}>
              Session credit {index + 1}
            </MenuItem>
          ) : null,
        )}
      </TextField>
      <TextField
        type="datetime-local"
        label="Session start (UTC)"
        size="small"
        disabled={busy}
        slotProps={{ inputLabel: { shrink: true } }}
        {...form.register("start")}
        required
      />
      <TextField
        type="datetime-local"
        label="Session end (UTC)"
        size="small"
        disabled={busy}
        slotProps={{ inputLabel: { shrink: true } }}
        {...form.register("end")}
        required
      />
      {lesson.courts
        .filter((court) =>
          lesson.schedule.allowed_court_ids.includes(court.court_id),
        )
        .map((court) => (
          <FormControlLabel
            key={court.court_id}
            label={court.name}
            control={
              <Checkbox
                disabled={!court.is_enabled || busy}
                checked={selected.includes(court.court_id)}
                onChange={(_, checked) =>
                  form.setValue(
                    "courts",
                    checked
                      ? [...selected, court.court_id]
                      : selected.filter((id) => id !== court.court_id),
                  )
                }
              />
            }
          />
        ))}
      {form.formState.errors.start ? (
        <Alert severity="error">{form.formState.errors.start.message}</Alert>
      ) : null}
      {book.isError ? (
        <Alert severity="error">
          The session could not be booked. Refresh the available credits and
          times before trying again.
        </Alert>
      ) : null}
      <Button type="submit" variant="contained" size="small" disabled={busy}>
        Book session
      </Button>
    </Stack>
  );
}
